import { computed, type IComputedValue } from 'mobx';
import loc, { format, type LocalizedString } from 'owa-localize';
import { lazyIsPrivateUnjoinedGroup } from 'owa-group-utils';
import { getGroupDisplayName } from 'owa-groups-shared-store/lib/selectors/getGroupDisplayName';
import { getModuleContextMailboxInfo } from 'owa-module-context-mailboxinfo';
import {
    lazyGetGroupFolderId,
    lazyGetGroupFolderTreeLoadingState,
} from 'owa-groups-shared-folders-store/lib/lazyFunctions';
import { GroupFolderTreeLoadStateEnum } from 'owa-groups-shared-folders-store/lib/store/schema/GroupFolderTreeLoadStateEnum';
import { type GroupFolderWellKnownName, PRIMARY_FOLDERS_TREE_TYPE } from 'owa-folders-constants';
import { getSelectedNode } from 'owa-mail-folder-forest-store';
import { isFolderOrSubFolderOfArchiveRoot } from 'owa-mail-store/lib/utils/isFolderUnderArchiveRoot';
import {
    SearchPlaceholder,
    SearchAriaLabel,
    SearchWithCopilotPlaceholder,
} from 'owa-search-locstrings/lib/searchPlaceholder.locstring.json';
import {
    SearchDisabledInGroup,
    GroupsSearchPlaceholder,
    SearchDisabled,
    archiveMailboxFoldersSearchPlaceholder,
    MOSAppSearchPlaceholder,
    MOSAppSearchPlaceholderFallback,
} from '../components/MailSearchBoxContainer.locstring.json';
import { isAccountExplicitLogon } from 'owa-account-shared-mailbox-utils';
import { shouldUseAppHostSearch } from 'owa-app-host-search/lib/utils/shouldUseAppHostSearch';
import { lazyGetAppDefinition_sync } from 'owa-app-definitions';
import { getSelectedApp } from 'owa-appbar-state';
import isWin32StylingEnabled from 'owa-theme-win32-style-store/lib/selectors/isWin32StylingEnabled';
import { FirstPartyAppId } from 'owa-m365-acquisitions/lib/data/M365AppId';
import { isSearchPlusChatFeatureEnabled } from 'owa-search/lib/utils/isSearchPlusChatFeatureEnabled';

/**
 * Determines if the search bar should be disabled
 */
export const isSearchBarDisabled: IComputedValue<boolean> = computed(() => {
    const mailboxInfo = getModuleContextMailboxInfo();
    if (mailboxInfo.type == 'GroupMailbox') {
        /* eslint-disable-next-line no-restricted-properties -- (https://aka.ms/OWALintWiki)
         * BASELINE. Do not copy and paste!
         *	> 'tryImportForRender' is restricted from being used. tryImportForRender should only be used in rare circumstances and can hurt TTI if used incorrectly. */
        const isPrivateUnjoinedGroup = lazyIsPrivateUnjoinedGroup.tryImportForRender();
        if (
            !isPrivateUnjoinedGroup ||
            isPrivateUnjoinedGroup(mailboxInfo.mailboxSmtpAddress, mailboxInfo)
        ) {
            return true;
        }

        const getGroupFolderTreeLoadingState =
            /* eslint-disable-next-line no-restricted-properties -- (https://aka.ms/OWALintWiki)
             * BASELINE. Do not copy and paste!
             *	> 'tryImportForRender' is restricted from being used. tryImportForRender should only be used in rare circumstances and can hurt TTI if used incorrectly. */
            lazyGetGroupFolderTreeLoadingState.tryImportForRender();
        const loadingState = getGroupFolderTreeLoadingState?.(
            mailboxInfo.mailboxSmtpAddress,
            mailboxInfo
        );

        if (loadingState && loadingState === GroupFolderTreeLoadStateEnum.Loaded) {
            /* eslint-disable-next-line no-restricted-properties -- (https://aka.ms/OWALintWiki)
             * BASELINE. Do not copy and paste!
             *	> 'tryImportForRender' is restricted from being used. tryImportForRender should only be used in rare circumstances and can hurt TTI if used incorrectly. */
            const getGroupFolderId = lazyGetGroupFolderId.tryImportForRender();
            const selectedNodeId = getGroupFolderId?.(
                mailboxInfo.mailboxSmtpAddress,
                'inbox',
                mailboxInfo
            );
            return !selectedNodeId;
        }
        return true;
    }
    return false;
});

// Get the right aria label for the narrator for the search box
export const getAriaLabelForSearchBox: IComputedValue<LocalizedString> = computed(() => {
    const mailboxInfo = getModuleContextMailboxInfo();
    // If in the primary mailbox scope and not in explicit logon, return the aria label for the search box with extended entity suggestions
    if (
        getSelectedNode()?.treeType == PRIMARY_FOLDERS_TREE_TYPE &&
        !isAccountExplicitLogon(mailboxInfo)
    ) {
        return loc(SearchAriaLabel);
    }

    // otherwise return the same aria label as the placeholder text
    return getSearchPlaceHolderText();
});

/**
 * Returns the placeholder text for the search box.
 */
export function getSearchPlaceHolderText(): LocalizedString {
    const mailboxInfo = getModuleContextMailboxInfo();
    const useAppHostSearch = shouldUseAppHostSearch();
    const isGroupMailbox = mailboxInfo.type == 'GroupMailbox' && !useAppHostSearch; // Don't use group mailbox context if in a metaOS app
    if (isSearchBarDisabled.get()) {
        return isGroupMailbox ? loc(SearchDisabledInGroup) : loc(SearchDisabled);
    }

    /**
     * If search box is not disabled, return the appropriate string depending
     * on if the user is in their primary or archive mailbox.
     */
    const folderForestStoreSelectedNode = getSelectedNode();
    if (
        folderForestStoreSelectedNode &&
        isFolderOrSubFolderOfArchiveRoot(folderForestStoreSelectedNode.id) &&
        !useAppHostSearch
    ) {
        return loc(archiveMailboxFoldersSearchPlaceholder);
    } else if (isGroupMailbox) {
        const groupSmtp = mailboxInfo.mailboxSmtpAddress;
        const groupName = groupSmtp ? getGroupDisplayName(mailboxInfo, groupSmtp) : null;
        if (groupName) {
            return format(loc(GroupsSearchPlaceholder), groupName);
        }
    } else if (useAppHostSearch) {
        const appId = getSelectedApp();
        /* eslint-disable-next-line no-restricted-properties -- (https://aka.ms/OWALintWiki)
         * BASELINE. Do not copy and paste!
         *	> 'tryImportForRender' is restricted from being used. tryImportForRender should only be used in rare circumstances and can hurt TTI if used incorrectly. */
        const getAppDefinition = lazyGetAppDefinition_sync.tryImportForRender();
        if (getAppDefinition) {
            const appName = getAppDefinition(appId, mailboxInfo)?.appDefinition?.name;
            return appName
                ? format(loc(MOSAppSearchPlaceholder), appName)
                : loc(MOSAppSearchPlaceholderFallback);
        }
    }

    if (
        !isWin32StylingEnabled() &&
        isSearchPlusChatFeatureEnabled(mailboxInfo) &&
        getSelectedApp() === FirstPartyAppId.Mail
    ) {
        return loc(SearchWithCopilotPlaceholder);
    }

    return loc(SearchPlaceholder);
}
